import { Component, InjectionKey } from 'vue';

import type { fileType } from '@/types/canned';

import { ProductType } from './broadcasts/broadcasts';

import type { channelType } from './channel';
import type { contactType } from './contact';
import type { customAttributeType } from './custom-attributes';
import type { metaTypes } from './general';
import type { tagType } from './tag';

// =============================================== Conversation related types ===============================================
// Main conversation type used across the application
export interface ConversationType {
  conversationId: string;
  status: (typeof ConversationsStatusConst)[keyof typeof ConversationsStatusConst];
  priority: (typeof PriorityValueConst)[keyof typeof PriorityValueConst];
  assigneeUser: ConversationsUserType | null;
  assigneeTeam: ConversationsTeamType | null;
  contact: ConversationContactType;
  closingNotes: customAttributeType[];
  channel: channelType;
  tags: tagType[];
  mentions: mentionsType[];
  lastMessage: string;
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
  broadcast: { id: string; name: string };
  participants: ConversationsUserType[];
  unreadMessagesCount: number;
  lastConversationMessageDate: string;
  lastHumanMessageDate: string;
  isSoftDeleted: boolean;
  workflow?: { id: string; name: string; status: (typeof WorkflowStatusEnum)[keyof typeof WorkflowStatusEnum] };
  pendingResponseDate: string | null;
  aiStatus: (typeof AiStatusEnum)[keyof typeof AiStatusEnum];
  lastSubscribedAt: string;
  isSubscribed: boolean;
  isMuted: boolean;
  isPinned: boolean;
  isRead: boolean;
  isAttendedConversation: boolean;
  // for chat widget channel
  isWebsiteClose: boolean;
  // for contact call
  lastCallData?: LastCallDataType;
}

export type endedCallStatus = 'answered' | 'missed' | 'unanswered' | 'busy';
export type callDirection = 'inbound' | 'outbound';

export interface LastCallDataType {
  callStatus: endedCallStatus | 'inProgress';
  direction: 'inbound' | 'outbound';
  agent: {
    id: string;
    name: string;
  };
  timestamps: {
    createdAt: number;
    ringingAt: number;
    answeredAt: number;
    endedAt: number | null;
  };
}

// Ai status type used in conversations, representing the status of AI agent in the conversation
export const AiStatusEnum = {
  RUNNING: 'running',
  ENDED: 'ended',
} as const;

// Workflow status enum
export const WorkflowStatusEnum = {
  START: 'start',
  ENDED: 'ended',
  PAUSED: 'paused',
  RESUMED: 'resumed',
  FAILED: 'failed',
} as const;

// Mentions type used in conversations, representing users mentioned in messages
export interface mentionsType {
  messageId: string;
  identityServiceId: string;
}

// Contact type used in conversations, representing the contact involved in the conversation
export interface ConversationContactType {
  id: string;
  contactId: string;
  contactServiceId: string;
  fullName: string;
  email: string;
  country: string;
  city: string;
  photo: string;
  customAttributes: customAttributeType[];
  tags: tagType[];
  isBlocked: boolean;
  companyName: string;
  bio: string;
  isDeleted: boolean;
  phoneNumber: string;
  channelName?: string;
}

// Assignee team type used in conversations, representing the team assigned to the conversation
export interface ConversationsTeamType {
  identityServiceId: string;
  name: string;
  id: string;
  _id?: string;
}

// Assignee user type used in conversations, representing the users assigned in the conversation
export interface ConversationsUserType {
  username: string;
  userName?: string;
  disabled?: boolean;
  selfAssign?: boolean;
  email: string;
  img: string;
  identityServiceId: string;
  id: string;
  _id?: string;
  userStatus?: string;
  aiAgent?: {
    id: string;
    isActive: boolean;
    name: string;
  };
}

// Conversation status type used in conversations, representing the status of the conversation
export const ConversationsStatusConst = {
  OPENED: 'Opened',
  RESOLVED: 'Resolved',
  PENDING: 'Pending',
} as const;

// Main data type for conversations list, used in conversations listing and pagination
export interface ConversationsDataType {
  options: Map<ConversationType['conversationId'], ConversationType>;
  nextCursor: string | null;
  assignmentCounts: AssignmentValueType;
  meta: metaTypes;
}

export interface CallLogContactType {
  id: string;
  contactServiceId: string;
  phoneNumber: string;
  name: string;
  lastCallStatus: string;
  lastCallDirection?: callDirection;
  channelName?: string;
  callTime?: string | number;
}

export interface CallLogsDataType {
  options: Map<CallLogContactType['contactServiceId'], CallLogContactType>;
  nextCursor: string | null;
  meta: metaTypes;
}

export interface ConversationsRequestParamsType {
  isLoadMore?: boolean;
  limit?: number;
}

export const RequestUrlDestinationConst = {
  MESSAGES: 'messages',
  MEDIA: 'media',
  GALLERY_MEDIA: 'galleryMedia',
} as const;

export interface ConversationsFetchOptions {
  page?: number;
  types?: string[];
  isLoadingMore?: boolean;
  messageId?: string | null;
  toLatestMessages?: boolean;
  limit?: number;
  isInitialLoad?: boolean;
}

export const ConversationRequestConst = {
  RESOLVE: 'resolve',
  PEND: 'pend',
  REOPEN: 'reopen',
  OPEN: 'open',
};

export interface ConversationStatusRequestDataType {
  name: string;
  value: (typeof ConversationRequestConst)[keyof typeof ConversationRequestConst];
}
// ==================================================================================================================================

// ============================================= Conversations filtration related types =============================================

// Custom filter relations
export type CustomFilterRelation = 'and' | 'or';
// Custom filter type used for advanced filtering of conversations based on multiple conditions and relations
export interface CustomFilterType {
  relation: CustomFilterRelation;
  filters: CustomFiltersType[];
  name?: string;
}

// Custom filters type used in conversations filtration, representing a set of filters with a specific relation (AND/OR)
export interface CustomFiltersType {
  filters: FiltersType[];
  id: string;
  relation: CustomFilterRelation;
}

// Filter type used in conversations filtration, representing a single filter condition with key, type, operator and value
export interface FiltersType {
  filter_key: string;
  filter_type: string;
  id: string;
  operator: string;
  // Record<string, string>[] for value set from redirect from clicking on user in dashboard
  value: null | string | string[] | Record<string, string>[];
  santized?: null | string | string[] | Record<string, string>[];
}

// Assignment counts constants and types used for representing the counts of conversations based on assignment status (all, mine, unassigned)
export const AssignmentCountsConst = {
  ALL: 'all',
  MINE: 'mine',
  UNASSIGNED: 'unassigned',
} as const;

export const AssignmentNumberCountsConst = {
  1: AssignmentCountsConst.ALL,
  2: AssignmentCountsConst.MINE,
  3: AssignmentCountsConst.UNASSIGNED,
} as const;

// Assignment value type used for representing the counts of conversations based on assignment status
export interface AssignmentValueType {
  [AssignmentCountsConst.ALL]: number;
  [AssignmentCountsConst.MINE]: number;
  [AssignmentCountsConst.UNASSIGNED]: number;
}

// Assignment filter type used for representing the filter options based on assignment status in the conversations listing sidebar
export interface AssignmentFilterType {
  name?: string;
  title?: string;
  count?: number;
  isFetching?: boolean;
  id: (typeof CustomFilterAssignTypeConst)[keyof typeof CustomFilterAssignTypeConst];
  key: (typeof AssignmentCountsConst)[keyof typeof AssignmentCountsConst];
}
// Custom filter assign type constants used for representing the filter options based on assignment status in the conversations listing sidebar
export const CustomFilterAssignTypeConst = {
  All: 1,
  AssignedToMe: 2,
  Unassigned: 3,
} as const;

export const ConversationsSortingConst = {
  LastActivityAt: 'lastActivityAt',
  Priority: 'priority',
  CreatedAt: 'createdAt',
  PendingResponse: 'pendingResponse',
} as const;

export type SidebarConversationsFilterKeyType =
  | 'tag'
  | 'channel'
  | 'team'
  | 'all-conversations'
  | 'mentions'
  | 'unattended'
  | 'workflow';

export const ConversationsStatusFiltrationConst = {
  ...ConversationsStatusConst,
  ALL: 'all',
} as const;

export interface ConversationsFilterSidebarType {
  type: SidebarConversationsFilterKeyType;
  id: string;
}

export interface SideBarFilterDataType {
  type: Exclude<SidebarConversationsFilterKeyType, 'all-conversations'>;
  id: string;
}

export interface CustomFilterSharingType {
  name: string;
  id: (typeof CustomFilterSharingValueConst)[keyof typeof CustomFilterSharingValueConst];
}

export const CustomFilterSharingValueConst = {
  PUBLIC: 'public',
  PRIVATE: 'private',
  CUSTOM: 'custom',
} as const;

export interface SharingWithOptionType {
  status: string | null;
  users: string[] | ConversationsUserType[] | null;
  teams: string[] | ConversationsTeamType[] | null;
}

export interface CustomFilterSharingWithDataType extends SharingWithOptionType {
  name: string | null;
}
// ==================================================================================================================================

// ============================================= UI states related types =============================================

export interface ConversationsUiFlagsType {
  isFetching: {
    users?: boolean;
    channels?: boolean;
    teams?: boolean;
    tags?: boolean;
    conversations?: boolean;
    customFilter?: boolean;
    searchLis?: boolean;
    prevConversations?: boolean;
    participantsList?: boolean;
    media?: boolean;
    galleryMedia?: boolean;
    messages?: boolean;
    conversation?: boolean;
    counter?: boolean;
    shortcuts?: boolean;
    customAttribute?: boolean;
    contactAttribute?: boolean;
    contacts?: boolean;
    aiTransformationText?: boolean;
  };
}

export interface ConversationsInitialUiFlagsType {
  initialLoad: {
    messages: boolean;
  };
}

export interface ErrorsFlagsType {
  isError: {
    media: boolean;
    galleryMedia: boolean;
    messages: boolean;
    conversations: boolean;
    conversation: boolean;
  };
}

export const MultiSelectActionConst = {
  TAGS: 'tags',
  STATUS: 'status',
} as const;

export const PriorityValueConst = {
  URGENT: 10,
  HIGH: 9,
  MEDIUM: 8,
  LOW: 7,
  NONE: 6,
} as const;

export interface PriorityDataType {
  id: (typeof PriorityValueConst)[keyof typeof PriorityValueConst];
  name: string;
}

export interface ReplayTypes {
  name: string;
  id: (typeof ReplyTypeConst)[keyof typeof ReplyTypeConst];
  icon: string;
  classes: string;
}

export const ReplyTypeConst = {
  Normal: 1,
  PrivateNote: 2,
};

export interface ConversationsStatusOption {
  name: string;
  value: string;
  id: number;
}

export interface ConversationsShortcutsType {
  description: string;
  id: string;
  name: string;
}

export interface ConversationsDateFilterType {
  name: string;
  value: string;
  id: number;
}

export interface AttachFilesOptionsType {
  name: string;
  id: number;
}

export interface TextareaActionsType {
  icon: Component | string;
  name: string;
  hint: string;
  id: number;
  includes?: string[];
  excludes?: string[];
}

export interface DisabledChannelStateData {
  text: string[];
  callBack: null | {
    method: () => void;
    text: string;
  };
}
// ==================================================================================================================================

// ============================================= Search related types =============================================

export interface SearchTabType {
  name: string;
  component: Component;
  id: (typeof SearchTabValueConst)[keyof typeof SearchTabValueConst];
}

export const SearchTabValueConst = {
  CONVERSATIONS: 'conversations',
  CONTACTS: 'contacts',
  MESSAGES: 'messages',
};

export interface SearchListDataType {
  options: Record<
    (typeof SearchTabValueConst)[keyof typeof SearchTabValueConst],
    (ConversationType | MessageType | contactType)[]
  >;
  meta: Record<(typeof SearchTabValueConst)[keyof typeof SearchTabValueConst], metaTypes>;
  currentPage: Record<(typeof SearchTabValueConst)[keyof typeof SearchTabValueConst], number>;
}

// ==================================================================================================================================

// ============================================= Messages related types =============================================
export const MessageSenderType = {
  CONTACT: 'contact',
  AGENT: 'agent',
  SYSTEM: 'system', // system = > workflow
  LOG: 'log',
  BROADCAST: 'broadcast',
  AI: 'ai',
  SESSION_DATE: 'sessiondate',
  SALLA: 'salla',
  CALL_LOG: 'call_log',
  DEVELOPER_API: 'developer_api',
  BUSINESS_APP: 'business_app',
} as const;

export interface AgentMessageType {
  senderType: typeof MessageSenderType.AGENT;
  agent: ConversationsUserType;
  contact?: never;
}

export interface ContactMessageType {
  senderType: typeof MessageSenderType.CONTACT;
  agent?: never;
  contact: ConversationContactType;
}

export interface RemainingMessageType {
  senderType:
    | typeof MessageSenderType.SYSTEM
    | typeof MessageSenderType.LOG
    | typeof MessageSenderType.BROADCAST
    | typeof MessageSenderType.AI
    | typeof MessageSenderType.SALLA
    | typeof MessageSenderType.DEVELOPER_API
    | typeof MessageSenderType.BUSINESS_APP;
  agent?: never;
  contact?: never;
}

export const MessageMediaType = { AUDIO: 'audio', IMAGE: 'image', VIDEO: 'video', DOCUMENT: 'document' } as const;
export const MessageTextType = { TEXT: 'text', LINK: 'link' } as const;

export const MessagesRemainingTypes = {
  LOGS: 'logs',
  TEMPLATE: 'template',
  CSAT: 'csat',
  LOCATION: 'location',
  ORDER: 'order',
  WHATSAPP_TEMPLATE: 'WhatsappTemplate',
  CONTACTS: 'contacts',
  SHARE_POST: 'share_post',
  INTERACTIVE: 'interactive',
  AD: 'ad',
  CALL_LOG: 'call_log',
} as const;

export const MessageContentTypes = {
  ...MessageMediaType,
  ...MessageTextType,
  ...MessagesRemainingTypes,
} as const;

interface BaseMessageContentType {
  isPrivate: boolean;
  isRetriedByAgent?: boolean;
  isFailed: boolean;
  errorMessage: string;
  value: string;
}

export interface TextMessageContentType extends BaseMessageContentType {
  messageType: typeof MessageTextType.TEXT | typeof MessageTextType.LINK;
  value: string;
}

export interface MediaMessageContentType extends BaseMessageContentType {
  messageType:
    | typeof MessageMediaType.AUDIO
    | typeof MessageMediaType.IMAGE
    | typeof MessageMediaType.VIDEO
    | typeof MessageMediaType.DOCUMENT;
  attachment: AttachmentType;
  value: string;
}

export interface TemplateMessageContentType extends BaseMessageContentType {
  messageType: typeof MessagesRemainingTypes.TEMPLATE | typeof MessagesRemainingTypes.WHATSAPP_TEMPLATE;
  templateValue: {
    sentTemplateDetails: SentTemplateDetails;
    sentMessageBodyDetails: string;
  };
  value: string;
}

export interface LocationMessageContentType extends BaseMessageContentType {
  messageType: typeof MessagesRemainingTypes.LOCATION;
  locationData: {
    latitude: number;
    longitude: number;
    name: string;
    address: string;
    url: string;
    _id: string;
  };
}

export interface OrderMessageContentType extends BaseMessageContentType {
  messageType: typeof MessagesRemainingTypes.ORDER;
  orderData: OrderDataType;
}

export interface ContactForMessageType extends ConversationContactType {
  phone: string;
  avatar: string;
}

export type ShareContactOutcome = 'updated' | 'matched';

export interface ContactsMessageContentType extends BaseMessageContentType {
  messageType: typeof MessagesRemainingTypes.CONTACTS;
  contactData: ContactForMessageType[];
  /** `null` for a normal contact share; set when the reply comes from a Share Contact Info template button. */
  shareContactOutcome?: ShareContactOutcome | null;
}

export interface SharePostMessageType extends BaseMessageContentType {
  messageType: typeof MessagesRemainingTypes.SHARE_POST;
  attachment: {
    value: string;
  };
}

export interface InteractiveMessageType extends BaseMessageContentType {
  messageType: typeof MessagesRemainingTypes.INTERACTIVE;
  value: string;
}

export interface LogMessageType extends BaseMessageContentType {
  messageType: typeof MessagesRemainingTypes.LOGS;
  value: string;
}

export interface SessionDateMessageType extends BaseMessageContentType {
  messageType: typeof MessageTextType.TEXT;
  value: string;
}

export interface CallLogMessageDataType extends BaseMessageContentType {
  messageType: typeof MessagesRemainingTypes.CALL_LOG;
  callLog: CallLogMessageType;
}

export interface DeveloperAPIDataType extends BaseMessageContentType {
  messageType: typeof MessagesRemainingTypes.WHATSAPP_TEMPLATE;
  templateValue: {
    sentTemplateDetails: SentTemplateDetails;
    sentMessageBodyDetails: string;
  };
  value: string;
}

export interface CsatMessageType extends BaseMessageContentType {
  messageType: typeof MessagesRemainingTypes.CSAT;
  csatInfo: {
    feedback: string;
    rating: number;
    timestamp: string;
  };
}

export type MessageContentType =
  | TextMessageContentType
  | MediaMessageContentType
  | TemplateMessageContentType
  | LocationMessageContentType
  | OrderMessageContentType
  | ContactsMessageContentType
  | InteractiveMessageType
  | SharePostMessageType
  | LogMessageType
  | CsatMessageType
  | SessionDateMessageType
  | CallLogMessageDataType
  | DeveloperAPIDataType;

export interface BaseMessageType {
  _id: string;
  channel: channelType;
  channelId: string;
  timestamp: string;
  conversationId: string;
  status: MessageStatusType;
  content: MessageContentType;
  isCsat?: boolean;
  virtualId?: string;
  parentMessage?: MessageType;
  messageProviderId?: string;
  globalMediaIndex?: string;
  isLoading?: boolean;
  conversationData?: ConversationType;
  referralData?: ConversationReferralType;
}

export type MessageType =
  | (AgentMessageType & BaseMessageType)
  | (ContactMessageType & BaseMessageType)
  | (RemainingMessageType & BaseMessageType);

interface BaseMessageSearchType {
  conversationId: string;
  channel: channelType;
  lastMessage: string;
  messageId: string;
  assigneeUser: ConversationsUserType;
  createdAt: string;
  content: string;
}

export type MessageSearchType =
  | (AgentMessageType & BaseMessageSearchType)
  | (ContactMessageType & BaseMessageSearchType)
  | (RemainingMessageType & BaseMessageSearchType);

export interface OrderDataType {
  catalogId: string;
  productItems: ProductType[];
  totalAmount: number;
  totalQuantity: number;
  currency: string;
}
export interface AttachmentType {
  type: (typeof MessageMediaType)[keyof typeof MessageMediaType];
  value: string | File;
  s3Key: string;
  name: string;
  fileSize?: number;
}

interface SentTemplateDetails {
  components: SentTemplateComponent[];
  language: string;
  category: string;
  meta_template_id: string;
  name: string;
  status: string;
  catalogName: string;
}

interface SentTemplateComponent {
  type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS' | 'LOCATION';
  format?: 'TEXT'; // Only HEADER has "format"
  original?: string; // Only HEADER and BODY may have "original"
  filled: string;
  id: string;
  url?: string; // Only HEADER type video, pdf or image has "url"
  value?: string; // Only HEADER type video, pdf or image has "value"
  text?: string; // Only FOOTER has "text"
  buttons?: {
    description: string;
    type: string;
    text: string;
  }[];
  location?: {
    googleMapsUrl: string;
    name: string;
    address: string;
  };
}

export interface ConversationReferralType {
  source_type: string;
  source_id: string;
  source_url: string;
  headline: string;
  body: string;
  media_type: string;
  image_url: string;
  video_url: string;
  thumbnail_url: string;
  image: ValueType;
  video: ValueType;
  thumbnail: ValueType;
}

export interface MessageStatusType {
  sent?: { timestamp: string; isCompleted: boolean };
  delivered?: { timestamp: string; isCompleted: boolean };
  read?: { timestamp: string; isCompleted: boolean };
  failed?: { timestamp: string; isCompleted: boolean; error: { code: string; message: string } };
}

export interface MessagesDataType {
  options: Map<MessageType['_id'], MessageType>;
  meta: metaTypes;
}

export interface SendMessagesType {
  [key: string]: SendMessageType;
}

export interface URLMetaDataType {
  type: string;
  name: string;
  url: string;
}

export interface SendMessageType {
  type: (typeof MessageMediaType)[keyof typeof MessageMediaType] | typeof MessageTextType.TEXT | 'csv';
  value?: string | File | Blob;
  fileId?: fileType;
  title?: string;
  mentions?: string[];
  meta_data?: URLMetaDataType;
}

export interface FileTypeMapping {
  [key: string]: string; // Extension: type (e.g., '.jpg': 'image')
}

export interface SizeLimits {
  [key: string]: number; // Type: size in MB (e.g., 'image': 5)
}

export interface SanitizedDataType {
  [key: string]: SendMessageType;
}

export type ChannelTypeName =
  | 'WhatsAppChannel'
  | 'FacebookChannel'
  | 'InstagramChannel'
  | 'GmailChannel'
  | 'TelegramChannel'
  | 'WebsiteChannel'
  | 'TikTokChannel';

export interface TextMessageType {
  meta_data?: URLMetaDataType;
  type: typeof MessageTextType.TEXT;
  value: string;
  mentions: string[];
}

export interface GalleryFilesType {
  id: number;
  title: string;
  value: string;
}

export interface MessageContactInfoType {
  id: string;
  name: string;
}

// not refactor yet
export interface ValueType {
  _id: string;
  messageType: string;
  s3Key: string;
  urlExpiresAt: string;
  value: string;
}

export interface MessageActions {
  openCatalog: (value: MessageType & { content: TemplateMessageContentType }) => void;
  openList: (value: MessageType & { content: TemplateMessageContentType }) => void;
}

export const messageActionsKey: InjectionKey<MessageActions> = Symbol('messageActions');

// ==================================================================================================================================

// ============================================= Conversations ai types =============================================

export type AiOptionsType = {
  name: string;
  icon: Component | string;
  childe?: Component;
  callBack: (value?: string | ToneValue) => void;
  disabled?: boolean;
};

export type ToneValue = 'professional' | 'friendly';

export type ToneOptionType = {
  name: string;
  value: ToneValue;
};

// ==================================================================================================================

// ============================================= Conversations settings =============================================
export type InactivityAutoClose = {
  enabled: boolean;
  value: number;
  unit: { name: string; value: InactivityAutoCloseUnit };
  durationInMinutes?: number;
};

export enum InactivityAutoCloseUnit {
  MINUTES = 'minutes',
  HOURS = 'hours',
  DAYS = 'days',
}

// ==================================================================================================================
// ============================================= Call Logs =============================================

export interface CallStatus {
  label: string;
  color: string;
  bg_color: string;
  icon: string;
}

export interface CallLogTranscriptDialogItem {
  speaker: number | string | boolean | null;
  text: string;
  startedAtMs: string;
  endedAtMs: string;
}

export interface CallLogMessageType {
  callStatus: endedCallStatus | 'inProgress';
  direction: callDirection;
  agent: {
    username: string;
  };
  duration: number;
  analysis: {
    summary: string;
    sentiment: 1 | 2 | 3 | 4 | 5;
    transcript: string;
  };
  audio: {
    url: string;
    expiresAt: number;
  };
  timestamps: {
    createdAt: number;
    ringingAt: number;
    answeredAt: number;
    endedAt: number;
  };
  hasAiCredits: boolean;
  artifacts?: {
    $__parent?: {
      artifacts: {
        analysis: {
          transcription: {
            dialog?: CallLogTranscriptDialogItem[];
          };
        };
      };
    };
    analysis?: {
      transcription: {
        dialog?: CallLogTranscriptDialogItem[];
      };
    };
  };
}

export interface LastCallLogMessageType {
  id: string;
  callId: string;
  direction: callDirection;
  voipPhoneNumber: string;
  customerNumber: string;
  channelName?: string;
  agent: {
    id: string;
    username: string;
    identityServiceId: string;
    img: string;
  };
  timestamps: {
    createdAt: number;
    ringingAt: number;
    answeredAt: number;
    endedAt: number;
  };
  duration: number;
}
// ==================================================================================================================
